// src/store/modules/template.js
import { DEFAULT_WEBSITE_FEATURES, normalizeWebsiteFeatures } from '@/config/websiteFeatures'
import { normalizeGlobalStyle, applyGlobalStyle } from '@/utils/websiteTheme'

// 公共头尾使用原始 HTML；JSON 配置和空占位不能作为页面内容输出。
function normalizeTemplateHtml(value) {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
        const parsed = JSON.parse(value);
        if (parsed === null || typeof parsed === 'object') return '';
    } catch (e) {
        // HTML 或普通文案不属于 JSON，保留原始内容。
    }
    return value;
}

const state = {
    headerInfo: {},
    bottomInfo: {},
    headerStatusInfo: {},
    bottomHtmlText: '',
    headerHtmlText: '',
    headerStatusHtmlText: '',
    templateData: null,
    globalStyle: {},
    frontendFeatures: { ...DEFAULT_WEBSITE_FEATURES },
}

const mutations = {
    // 设置主题
    SET_THEME_COLORS(state, colors) {
        applyGlobalStyle(state, normalizeGlobalStyle(colors));
    },

    // 设置浏览器标题和图标
    SET_BROWSER_TITLE_AND_ICON(state, globalStyleData) {
        // 设置浏览器标题
        if (globalStyleData.titleName) {
            document.title = globalStyleData.titleName;

            // 设置 <meta name="title">（可选）
            let metaTitle = document.querySelector('meta[name="title"]');
            if (!metaTitle) {
                metaTitle = document.createElement('meta');
                metaTitle.name = 'title';
                document.head.appendChild(metaTitle);
            }
            metaTitle.setAttribute('content', globalStyleData.titleName);
        }

        // 设置 Open Graph 标题（关键！用于微信/微博分享）
        let ogTitle = document.querySelector('meta[property="og:title"]');
        if (!ogTitle) {
            ogTitle = document.createElement('meta');
            ogTitle.setAttribute('property', 'og:title');
            document.head.appendChild(ogTitle);
        }
        ogTitle.setAttribute('content', globalStyleData.titleName || '默认标题');

        // 设置 Open Graph 描述（可选）
        let ogDesc = document.querySelector('meta[property="og:description"]');
        if (!ogDesc) {
            ogDesc = document.createElement('meta');
            ogDesc.setAttribute('property', 'og:description');
            document.head.appendChild(ogDesc);
        }
        ogDesc.setAttribute('content', globalStyleData.description || '物业协会官网');

        // 设置浏览器图标
        if (globalStyleData.titleIcon) {
            // 查找已存在的 favicon 链接
            let favicon = document.querySelector('link[rel="icon"]');
            if (favicon) {
                // 如果存在则更新 href
                favicon.href = globalStyleData.titleIcon;
            } else {
                // 如果不存在则创建新的 link 标签
                favicon = document.createElement('link');
                favicon.rel = 'icon';
                favicon.href = globalStyleData.titleIcon;
                document.head.appendChild(favicon);
            }
        }
    },
    SET_TEMPLATE_DATA(state, data) {
        state.templateData = data;
        state.frontendFeatures = normalizeWebsiteFeatures(data?.resultData?.frontendFeatures);
    },

    SET_HEADER_INFO(state, headerInfo) {
        state.headerInfo = headerInfo;
    },

    SET_BOTTOM_INFO(state, bottomInfo) {
        state.bottomInfo = bottomInfo;
    },

    SET_HEADER_STATUS_INFO(state, headerStatusInfo) {
        state.headerStatusInfo = headerStatusInfo;
    },

    SET_BOTTOM_HTML_TEXT(state, htmlText) {
        state.bottomHtmlText = normalizeTemplateHtml(htmlText);
    },

    SET_HEADER_HTML_TEXT(state, htmlText) {
        state.headerHtmlText = normalizeTemplateHtml(htmlText);
    },

    SET_HEADER_STATUS_HTML_TEXT(state, htmlText) {
        state.headerStatusHtmlText = normalizeTemplateHtml(htmlText);
    },

    CLEAR_TEMPLATE_DATA(state) {
        applyGlobalStyle(state, {});
        state.headerInfo = {};
        state.bottomInfo = {};
        state.headerStatusInfo = {};
        state.bottomHtmlText = '';
        state.headerHtmlText = '';
        state.headerStatusHtmlText = '';
        state.templateData = null;
        state.frontendFeatures = { ...DEFAULT_WEBSITE_FEATURES };
    }
}

const actions = {
    setTemplateData({ commit }, templateData) {
        // 设计器已兼容旧 extra 层；官网也先归一化，再供主题和页面背景等消费者读取。
        const sourceList = templateData?.resultData?.moduleComponentList;
        if (Array.isArray(sourceList)) {
            templateData = {
                ...templateData,
                resultData: {
                    ...templateData.resultData,
                    moduleComponentList: sourceList.map(module => module && module.moduleCode === 'globalStyle'
                        ? { ...module, moduleStyleData: JSON.stringify(normalizeGlobalStyle(module.moduleStyleData)) }
                        : module),
                },
            };
        }
        commit('SET_TEMPLATE_DATA', templateData);
        // 无全局模块或配置异常时也清理旧模板的主题，不能延用其他站点的值。
        const globalModule = Array.isArray(sourceList)
            ? templateData.resultData.moduleComponentList.find(module => module && module.moduleCode === 'globalStyle')
            : null;
        const globalStyleData = normalizeGlobalStyle(globalModule?.moduleStyleData);
        commit('SET_THEME_COLORS', globalStyleData);
        if (globalModule) commit('SET_BROWSER_TITLE_AND_ICON', globalStyleData);

        // 解析并设置各个模块信息
        if (templateData && templateData.resultData && templateData.resultData.moduleComponentList) {
            const moduleList = templateData.resultData.moduleComponentList;

            const headerInfo = moduleList.filter(item => item.moduleCode === 'headerBackground');
            const headerStatusInfo = moduleList.filter(item => item.moduleCode === 'headerInformation');
            const bottomInfo = moduleList.filter(item => item.moduleCode === 'bottom');

            commit('SET_HEADER_INFO', headerInfo);
            commit('SET_HEADER_STATUS_INFO', headerStatusInfo);
            commit('SET_BOTTOM_INFO', bottomInfo);

            // 设置 HTML 内容
            commit('SET_BOTTOM_HTML_TEXT', bottomInfo[0]?.componentList?.[0]?.styleData);
            commit('SET_HEADER_HTML_TEXT', headerInfo[0]?.componentList?.[0]?.styleData);
            commit('SET_HEADER_STATUS_HTML_TEXT', headerStatusInfo[0]?.componentList?.[0]?.styleData);

        }
    },

    clearTemplateData({ commit }) {
        commit('CLEAR_TEMPLATE_DATA');
    }
}

const getters = {
    topCompanyLoginEnabled: state => state.frontendFeatures.topCompanyLoginEnabled,
    serviceIntegrationEnabled: state => state.frontendFeatures.serviceIntegrationEnabled,
    operationGuideWebsiteEnabled: state => state.frontendFeatures.operationGuideWebsiteEnabled,
    operationGuideServicePlatformEnabled: state => state.frontendFeatures.operationGuideServicePlatformEnabled,
    operationGuideEntryReady: state => String(state.templateData?.resultCode) === '200' &&
        !!state.templateData?.resultData && typeof state.templateData.resultData === 'object' && !Array.isArray(state.templateData.resultData),
    templateData: state => state.templateData,
    headerInfo: state => state.headerInfo,
    bottomInfo: state => state.bottomInfo,
    headerStatusInfo: state => state.headerStatusInfo,
    bottomHtmlText: state => state.bottomHtmlText,
    headerHtmlText: state => state.headerHtmlText,
    headerStatusHtmlText: state => state.headerStatusHtmlText,

    // 获取特定模块的信息
    getModuleByCode: (state) => (moduleCode) => {
        if (!state.templateData || !state.templateData.resultData) return null;

        return state.templateData.resultData.moduleComponentList.find(
            item => item.moduleCode === moduleCode
        );
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
